import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, PageHeader, Pill, EmptyState } from "@/components/common";
import { toast } from "sonner";
import { Megaphone, Plus, TrendingUp } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CHANNELS = ["Meta", "TikTok", "Google", "Pinterest"];
const STATUS_TONE = { proposed: "amber", running: "emerald", rejected: "red" };

export default function Ads() {
  const [ads, setAds] = useState([]);
  const [name, setName] = useState("");
  const [budget, setBudget] = useState(50);
  const [channel, setChannel] = useState("Meta");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/ads").then(({ data }) => setAds(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const propose = async () => {
    if (!name.trim()) { toast.error("Name the campaign first."); return; }
    setLoading(true);
    try {
      await api.post("/ads/propose", { name, budget: Number(budget), channel });
      toast.success("Campaign proposed — sent to Review Queue for spend approval.");
      window.dispatchEvent(new Event("queue-changed"));
      setName("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Proposal failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader title="Ad Campaigns" subtitle="The agent proposes ad tests; every dollar of spend is approval-gated in the Review Queue." testid="ads-header">
        <Pill tone="amber">Money Gate</Pill>
      </PageHeader>

      <Card className="p-5" data-testid="ad-input-card">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <input data-testid="ad-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Campaign name, e.g. 'Cat Mug — cold audience test'" className="lg:col-span-5 px-3 py-2.5 rounded-lg bg-[#12161F] border border-slate-800 focus:border-emerald-500/60 outline-none text-sm" />
          <div className="lg:col-span-3 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#12161F] border border-slate-800">
            <span className="text-slate-500 text-sm">$</span>
            <input data-testid="ad-budget-input" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" />
            <span className="text-slate-500 text-xs">/day</span>
          </div>
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger data-testid="ad-channel-select" className="lg:col-span-2 bg-[#12161F] border-slate-800 text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#12161F] border-slate-800 text-slate-100">
              {CHANNELS.map((c) => <SelectItem key={c} value={c} className="focus:bg-slate-800">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <button data-testid="propose-ad-button" onClick={propose} disabled={loading} className="lg:col-span-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-60 flex items-center gap-2 justify-center">
            <Plus className="w-4 h-4" /> Propose
          </button>
        </div>
      </Card>

      {ads.length === 0 ? (
        <Card className="p-6"><EmptyState icon={Megaphone} title="No campaigns yet" hint="Propose an ad test — it will require your approval before any spend." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {ads.map((a) => (
            <Card key={a.id} className="p-5" data-testid={`ad-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-head text-base font-semibold text-slate-100">{a.name}</h3>
                <Pill tone={STATUS_TONE[a.status] || "slate"}>{a.status}</Pill>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div><div className="text-lg font-head font-bold text-slate-100">${a.budget}</div><div className="text-[10px] text-slate-500 font-mono uppercase">Budget/day</div></div>
                <div><div className="text-lg font-head font-bold text-slate-100">{a.channel}</div><div className="text-[10px] text-slate-500 font-mono uppercase">Channel</div></div>
                <div><div className="text-lg font-head font-bold text-emerald-400 flex items-center justify-center gap-1"><TrendingUp className="w-4 h-4" />{a.est_roas}x</div><div className="text-[10px] text-slate-500 font-mono uppercase">Est. ROAS</div></div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
